package es.subastia.profile

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import es.subastia.domain.model.UserDomainModel
import es.subastia.domain.usecase.LogoutUseCase
import es.subastia.domain.usecase.ObserveAuthStateUseCase
import es.subastia.domain.usecase.SignInWithEmailUseCase
import es.subastia.domain.usecase.SignInWithGoogleUseCase
import es.subastia.domain.usecase.SignUpWithEmailUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

class ProfileViewModel(
    private val observeAuthStateUseCase: ObserveAuthStateUseCase,
    private val signInWithEmailUseCase: SignInWithEmailUseCase,
    private val signUpWithEmailUseCase: SignUpWithEmailUseCase,
    private val signInWithGoogleUseCase: SignInWithGoogleUseCase,
    private val logoutUseCase: LogoutUseCase,
) : ViewModel() {

    val currentUser: StateFlow<UserDomainModel?> = observeAuthStateUseCase()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), null)

    private val _uiState = MutableStateFlow<ProfileUiState>(ProfileUiState.Idle)
    val uiState: StateFlow<ProfileUiState> = _uiState.asStateFlow()

    fun signInWithEmail(email: String, pass: String) {
        viewModelScope.launch {
            _uiState.value = ProfileUiState.Loading
            try {
                signInWithEmailUseCase(email, pass)
                _uiState.value = ProfileUiState.Idle
            } catch (e: Exception) {
                _uiState.value = ProfileUiState.Error(e.message ?: "Login failed")
            }
        }
    }

    fun signUpWithEmail(email: String, pass: String) {
        viewModelScope.launch {
            _uiState.value = ProfileUiState.Loading
            try {
                signUpWithEmailUseCase(email, pass)
                _uiState.value = ProfileUiState.Idle
            } catch (e: Exception) {
                _uiState.value = ProfileUiState.Error(e.message ?: "Signup failed")
            }
        }
    }

    fun logout() {
        viewModelScope.launch {
            try {
                logoutUseCase()
            } catch (e: Exception) {
                // Ignore logout errors for now
            }
        }
    }

    fun signInWithGoogle(idToken: String) {
        viewModelScope.launch {
            _uiState.value = ProfileUiState.Loading
            try {
                signInWithGoogleUseCase(idToken)
                _uiState.value = ProfileUiState.Idle
            } catch (e: Exception) {
                _uiState.value = ProfileUiState.Error(e.message ?: "Google sign-in failed")
            }
        }
    }

    fun clearError() {
        _uiState.value = ProfileUiState.Idle
    }

    fun setError(message: String) {
        _uiState.value = ProfileUiState.Error(message)
    }
}

sealed interface ProfileUiState {
    data object Idle : ProfileUiState
    data object Loading : ProfileUiState
    data class Error(val message: String) : ProfileUiState
}
